/**
 * CanniComply shared UI primitives.
 *
 * These components exist to keep every dashboard page speaking the same
 * visual language: same corner radius, same border colour, same spacing,
 * same button hierarchy. They are deliberately unopinionated wrappers on
 * top of Tailwind + lucide — no runtime overhead, no theming provider —
 * so any page can drop them in without a big migration.
 *
 * Design tokens (kept implicit, not exported as CSS vars, so we stay
 * shadcn-compatible):
 *   Navy   #0B1220   — primary text + dark surfaces
 *   Gold   #D4A65A   — brand accent + primary CTAs on light surfaces
 *   Gold+  #8C6B33   — accessible text on gold surfaces
 *   Cream  #FDF6E7   — gold-tinted background wash
 *   Border slate-200 — neutral card border
 *   Radius rounded-xl  (12px) for cards, rounded-lg (8px) for controls
 */

import { Link } from "react-router-dom";
import { Loader2, ArrowRight, AlertTriangle } from "lucide-react";

/* --------------------------------- Card ---------------------------------- *
 * Standard content container. Tone:
 *   - default : white / slate-200 border
 *   - subtle  : slate-50 wash
 *   - gold    : cream + gold border (for highlights)
 *   - dark    : navy hero surface
 * Padding controlled via `p`.
 * ------------------------------------------------------------------------- */
export function Card({
  as = "div",
  tone = "default",
  padding = "md",
  className = "",
  children,
  ...rest
}) {
  const Tag = as;
  const toneMap = {
    default: "bg-white border border-slate-200",
    subtle: "bg-slate-50/60 border border-slate-200",
    gold: "bg-[#FDF6E7] border border-[#D4A65A]/40",
    dark: "bg-gradient-to-br from-[#0B1220] via-[#0F1730] to-[#131c31] text-white border border-white/10",
    emerald: "bg-emerald-50/60 border border-emerald-200",
    rose: "bg-rose-50/60 border border-rose-200",
    amber: "bg-amber-50/60 border border-amber-200",
  };
  const padMap = {
    none: "",
    sm: "p-3.5",
    md: "p-5",
    lg: "p-6",
    xl: "p-6 md:p-8",
  };
  return (
    <Tag
      className={`rounded-xl ${toneMap[tone] || toneMap.default} ${padMap[padding] || padMap.md} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* ----------------------------- Section header ---------------------------- *
 * Consistent block heading with an optional kicker line and trailing action.
 * ------------------------------------------------------------------------- */
export function SectionHeader({
  kicker,
  title,
  description,
  icon: Icon,
  action,
  size = "md",
  testid,
  className = "",
}) {
  const titleClass =
    size === "lg"
      ? "font-display text-2xl md:text-3xl tracking-tight text-slate-900 font-semibold"
      : "font-display text-[15px] tracking-tight text-slate-900 font-semibold";
  return (
    <div
      data-testid={testid}
      className={`flex items-start justify-between gap-3 ${className}`}
    >
      <div className="min-w-0">
        {kicker && (
          <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-[#8C6B33] font-semibold">
            {Icon && <Icon size={12} />}
            {kicker}
          </div>
        )}
        <div className={`${kicker ? "mt-1.5" : ""} flex items-center gap-2`}>
          {!kicker && Icon && <Icon size={size === "lg" ? 18 : 14} className="text-[#8C6B33]" />}
          <h2 className={titleClass}>{title}</h2>
        </div>
        {description && (
          <p className="mt-1.5 text-[13.5px] text-slate-500 leading-relaxed max-w-2xl">
            {description}
          </p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

/* ------------------------------ Page header ------------------------------ *
 * The top-of-page block every dashboard route uses.
 * ------------------------------------------------------------------------- */
export function PageHeader({
  kicker,
  title,
  description,
  action,
  testid,
  className = "",
}) {
  return (
    <header
      data-testid={testid}
      className={`mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4 ${className}`}
    >
      <div className="min-w-0 flex-1">
        {kicker && (
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
            {kicker}
          </div>
        )}
        <h1 className="mt-2 font-display text-3xl md:text-4xl leading-tight tracking-tight text-slate-900 font-semibold break-words [overflow-wrap:anywhere]">
          {title}
        </h1>
        {description && (
          <p className="mt-2 text-slate-500 text-[15px] max-w-2xl leading-relaxed break-words">
            {description}
          </p>
        )}
      </div>
      {action && (
        <div className="shrink-0 flex flex-wrap items-center gap-2">
          {action}
        </div>
      )}
    </header>
  );
}

/* --------------------------------- Button -------------------------------- *
 * A single Button primitive so every page uses the same hierarchy:
 *   primary   — filled navy (default CTA)
 *   accent    — gold (marketing CTAs, cert downloads)
 *   secondary — outlined slate (secondary action)
 *   ghost     — text-only
 *   danger    — rose (destructive)
 * Sizes: sm, md, lg. Icon prop puts an icon before the label. `to` renders a
 * <Link>; otherwise a <button>.
 * ------------------------------------------------------------------------- */
export function Button({
  variant = "primary",
  size = "md",
  icon: Icon,
  iconRight: IconRight,
  loading = false,
  disabled = false,
  to,
  href,
  type = "button",
  className = "",
  children,
  ...rest
}) {
  const variantMap = {
    primary:
      "bg-[#0B1220] hover:bg-[#131c31] text-white border border-transparent",
    accent:
      "bg-[#D4A65A] hover:bg-[#E6BE7A] text-[#0B1220] border border-transparent",
    secondary:
      "bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 hover:border-slate-300",
    ghost:
      "bg-transparent hover:bg-slate-100 text-slate-700 hover:text-slate-900 border border-transparent",
    danger:
      "bg-rose-600 hover:bg-rose-700 text-white border border-transparent",
    "danger-ghost":
      "bg-transparent hover:bg-rose-50 text-rose-700 border border-transparent",
    emerald:
      "bg-emerald-600 hover:bg-emerald-700 text-white border border-transparent",
  };
  const sizeMap = {
    sm: "text-[12px] px-2.5 py-1.5 rounded-md gap-1",
    md: "text-[12.5px] px-3.5 py-2 rounded-lg gap-1.5",
    lg: "text-[13.5px] px-4 py-2.5 rounded-lg gap-2",
  };
  const iconSize = size === "sm" ? 12 : size === "lg" ? 15 : 13;
  const cls = `inline-flex items-center justify-center font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#D4A65A]/40 ${variantMap[variant] || variantMap.primary} ${sizeMap[size] || sizeMap.md} ${className}`;

  const content = (
    <>
      {loading ? (
        <Loader2 size={iconSize} className="animate-spin" />
      ) : (
        Icon && <Icon size={iconSize} />
      )}
      <span>{children}</span>
      {IconRight && !loading && <IconRight size={iconSize} />}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={cls} {...rest}>
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={cls} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <button type={type} disabled={disabled || loading} className={cls} {...rest}>
      {content}
    </button>
  );
}

/* ------------------------------- StatusPill ------------------------------ *
 * Small, opinionated status pill with predefined tones.
 * ------------------------------------------------------------------------- */
export function StatusPill({ tone = "slate", children, icon: Icon, testid, className = "" }) {
  const toneMap = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    gold: "bg-[#FDF6E7] text-[#8C6B33] border-[#D4A65A]/40",
    navy: "bg-[#0B1220] text-[#D4A65A] border-transparent",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.14em] font-semibold border rounded px-2 py-0.5 ${toneMap[tone] || toneMap.slate} ${className}`}
    >
      {Icon && <Icon size={10} />}
      {children}
    </span>
  );
}

/* -------------------------------- StatCard ------------------------------- *
 * Metric tile with label + value + optional icon + tone.
 * ------------------------------------------------------------------------- */
export function StatCard({
  label,
  value,
  icon: Icon,
  tone = "slate",
  hint,
  testid,
  className = "",
}) {
  const toneMap = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
    gold: "text-[#8C6B33]",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border border-slate-200 bg-white p-4 ${className}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
        {Icon && <Icon size={11} />}
        {label}
      </div>
      <div className={`mt-1.5 font-display text-2xl font-semibold ${toneMap[tone] || toneMap.slate}`}>
        {value}
      </div>
      {hint && <div className="mt-1 text-[11.5px] text-slate-500">{hint}</div>}
    </div>
  );
}

/* ------------------------------ EmptyState ------------------------------- *
 * Consistent empty-state block with icon, headline, copy and optional CTA.
 * ------------------------------------------------------------------------- */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  testid,
  className = "",
}) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center ${className}`}
    >
      {Icon && (
        <span className="mx-auto h-11 w-11 rounded-lg bg-[#0B1220] text-[#D4A65A] flex items-center justify-center">
          <Icon size={18} />
        </span>
      )}
      <div className="mt-4 font-display text-lg text-slate-900 font-semibold">{title}</div>
      {description && (
        <p className="mt-2 text-slate-500 text-[13.5px] max-w-md mx-auto leading-relaxed">
          {description}
        </p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/* --------------------------- LoadingRow / Skeleton ----------------------- */
export function LoadingRow({ label = "Loading…", testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500 text-sm flex items-center justify-center gap-2"
    >
      <Loader2 className="animate-spin" size={16} /> {label}
    </div>
  );
}

/* ------------------------------ ErrorBanner ------------------------------ *
 * Consistent inline error banner with a user-friendly wrapper. Pass the raw
 * error string in `error` — the banner will hide itself if empty.
 * ------------------------------------------------------------------------- */
export function ErrorBanner({ error, title, testid = "error-banner", className = "" }) {
  if (!error) return null;
  return (
    <div
      data-testid={testid}
      className={`rounded-md border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-2.5 ${className}`}
    >
      <AlertTriangle size={15} className="text-rose-600 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1 text-[13px] text-rose-800 leading-relaxed">
        {title && <div className="font-semibold mb-0.5">{title}</div>}
        <div className="opacity-90">{humanizeError(error)}</div>
      </div>
    </div>
  );
}

/* Successful-action banner (green, dismissable via onClose). */
export function SuccessBanner({ notice, testid, className = "" }) {
  if (!notice) return null;
  return (
    <div
      data-testid={testid}
      className={`rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] text-emerald-800 ${className}`}
    >
      {notice}
    </div>
  );
}

/* Tiny "kicker" label used across pages. */
export function Kicker({ children, className = "", tone = "slate" }) {
  const toneMap = {
    slate: "text-slate-500",
    gold: "text-[#8C6B33]",
    emerald: "text-emerald-700",
  };
  return (
    <div
      className={`inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] font-semibold ${toneMap[tone] || toneMap.slate} ${className}`}
    >
      {children}
    </div>
  );
}

/* ---------------------------------- Row --------------------------------- *
 * A generic clickable list row used across list surfaces. Left icon, main
 * content slot, right action slot. Renders as a Link when `to` is provided.
 * ------------------------------------------------------------------------- */
export function Row({
  as = "div",
  to,
  onClick,
  icon,
  action,
  children,
  className = "",
  testid,
}) {
  const Tag = to ? Link : onClick ? "button" : as;
  const props = to ? { to } : onClick ? { onClick, type: "button" } : {};
  return (
    <Tag
      data-testid={testid}
      className={`w-full text-left rounded-xl border border-slate-200 bg-white p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4 hover:border-slate-300 hover:shadow-[0_2px_20px_-8px_rgba(11,18,32,0.12)] transition-all ${className}`}
      {...props}
    >
      {icon && <div className="shrink-0">{icon}</div>}
      <div className="flex-1 min-w-0">{children}</div>
      {action && (
        <div className="shrink-0 flex items-center gap-2">
          {action}
          {to && <ArrowRight size={12} className="text-slate-400" />}
        </div>
      )}
    </Tag>
  );
}

/* --------------------------- humanizeError helper ------------------------ *
 * Translate common raw backend / network errors into copy a non-technical
 * user can act on. Keeps ErrorBanner useful even when a page passes it a
 * raw fetch error.
 * ------------------------------------------------------------------------- */
export function humanizeError(err) {
  if (!err) return "";
  const s = typeof err === "string" ? err : err.message || String(err);
  const lower = s.toLowerCase();
  if (lower.includes("failed to fetch") || lower.includes("networkerror")) {
    return "We couldn't reach the server. Check your connection and try again.";
  }
  if (lower.startsWith("401") || lower.includes("not authenticated")) {
    return "Your session has expired. Please sign in again.";
  }
  if (lower.startsWith("403") || lower.includes("forbidden") || lower.includes("admin-only")) {
    return "You don't have permission to do that.";
  }
  if (lower.startsWith("404") || lower.includes("not found")) {
    return "We couldn't find that record. It may have been removed.";
  }
  if (lower.startsWith("409") || lower.includes("conflict")) {
    // Return the server-provided message — it's usually already the most
    // useful thing (e.g. "No approved assessment is available…").
    return s.replace(/^\s*409[:\s-]*/i, "");
  }
  if (lower.startsWith("500") || lower.includes("internal server error")) {
    return "Something went wrong on our end. Please try again — if it persists, contact support.";
  }
  if (lower.includes("demo data is read-only")) {
    return "You're in the demo workspace, so this action is read-only. Create your workspace to make changes.";
  }
  return s;
}
